/**
 * Supplemental documents multi-select on the Policy Settings page (#34376).
 *
 * An underwriter can attach one or more optional supplemental documents to a farm policy; the
 * available documents are configured per program in brand-config (brandConfig.config
 * .supplementalDocuments). The selection is stored on the policy as an array of document ids
 * (policy.supplementalDocuments) and clears at renewal.
 */

import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

/**
 * Build the chipArray options from a brand's supplemental-documents catalog.
 * A `migratedRenewalsOnly` document (Forreston's Branch Agreement) is only offered on a
 * migrated-renewal policy (policy.wasRenewedFromImportedTerm).
 * When `policyType` is given, an entry is offered only when that type is in its `policyTypes`
 * scope — absent scope means farm-based only (F/Z/Y); CFM's Recurring Payment Authorization adds
 * 'A' (#35891). Mirrors policy-mod's sanitizeSupplementalDocuments and docs-engine's resolver.
 *
 * @param {Array<{id: string, label: string, migratedRenewalsOnly?: boolean, policyTypes?: string[]}>} catalog
 * @param {{ isMigratedRenewal?: boolean, policyType?: string }} [opts]
 * @returns {Array<{id: string, value: string}>}
 */
const appliesToPolicyType = (doc, policyType) => {
  if (!policyType) {
    return true;
  }
  return doc.policyTypes ? doc.policyTypes.includes(policyType) : isFarmBasedPolicyType(policyType);
};

export const buildSupplementalDocumentOptions = (catalog, { isMigratedRenewal = false, policyType } = {}) =>
  (catalog ?? [])
    .filter((doc) => !doc.migratedRenewalsOnly || isMigratedRenewal)
    .filter((doc) => appliesToPolicyType(doc, policyType))
    .map((doc) => ({ id: doc.id, value: doc.label }));
